import {
  ReactNode,
  createContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { throttle } from 'lodash';
import { InterfaceButton } from '@tiendanube/components';
import { useBoolean } from 'commons/hooks';

const noop = () => null;

type HeaderNavigaionType =
  | Pick<InterfaceButton, 'children' | 'onClick'>
  | undefined;

export interface InterfaceTopbarContext {
  headerNavigation: HeaderNavigaionType;
  setHeaderNavigation: (headerNavigation: HeaderNavigaionType) => void;
  noReadMessagesCount: number;
  setNoReadMessagesCount: (count: number) => void;
  height: number;
  setHeight: (height: number) => void;
  isHelpWidgetOpen: boolean;
  toggleHelpWidget: () => void;
  isShowMenu: boolean;
  showMenu: () => void;
  hideMenu: () => void;
  isShowTopbar: boolean;
  showTopbar: () => void;
  hideTopbar: () => void;
  startScrollListener: () => void;
  stopScrollListener: () => void;
}

export interface InterfaceTopbarContextProvider {
  children: ReactNode;
}

export const TopbarContext = createContext<InterfaceTopbarContext>({
  headerNavigation: undefined,
  setHeaderNavigation: noop,
  noReadMessagesCount: 0,
  setNoReadMessagesCount: noop,
  height: 0,
  setHeight: noop,
  isHelpWidgetOpen: false,
  toggleHelpWidget: noop,
  isShowMenu: true,
  showMenu: noop,
  hideMenu: noop,
  isShowTopbar: true,
  showTopbar: noop,
  hideTopbar: noop,
  startScrollListener: noop,
  stopScrollListener: noop,
});

function TopbarContextProvider({
  children,
}: InterfaceTopbarContextProvider): JSX.Element {
  const scrollTopPrev = useRef<number>(0);
  const [header, setHeader] = useState<HeaderNavigaionType>();
  const [isShowTopbar, showTopbar, hideTopbar] = useBoolean(true);
  const [isShowMenu, showMenu, hideMenu] = useBoolean(true);
  const [height, setHeight] = useState(0);
  const [noReadMessagesCount, setNoReadMessagesCount] = useState(0);
  const [isHelpWidgetOpen, setIsHelpWidgetOpen] = useState(false);
  const [isScrollListenerActive, setIsScrollListenerActive] = useState(false);

  const setHeaderNavigation = useCallback(
    (headerNavigation) => setHeader(headerNavigation),
    [],
  );

  const startScrollListener = useCallback(
    () => setIsScrollListenerActive(true),
    [],
  );
  const stopScrollListener = useCallback(
    () => setIsScrollListenerActive(false),
    [],
  );

  const toggleHelpWidget = useCallback(
    () => setIsHelpWidgetOpen((prevState) => !prevState),
    [],
  );

  const memoizedValue = useMemo(
    () => ({
      headerNavigation: header,
      setHeaderNavigation,
      noReadMessagesCount,
      setNoReadMessagesCount,
      height,
      setHeight,
      isHelpWidgetOpen,
      toggleHelpWidget,
      isShowMenu,
      showMenu,
      hideMenu,
      isShowTopbar,
      showTopbar,
      hideTopbar,
      startScrollListener,
      stopScrollListener,
    }),
    [
      header,
      setHeaderNavigation,
      noReadMessagesCount,
      setNoReadMessagesCount,
      height,
      setHeight,
      isHelpWidgetOpen,
      toggleHelpWidget,
      isShowMenu,
      showMenu,
      hideMenu,
      isShowTopbar,
      showTopbar,
      hideTopbar,
      startScrollListener,
      stopScrollListener,
    ],
  );

  useEffect(() => {
    if (!isScrollListenerActive) {
      return;
    }
    /**
     * Handles the scroll event to show or hide the topbar based on the scroll direction.
     *
     * @param {Event} e - The scroll event.
     *
     * The function checks the scroll position of the document and compares it with the previous scroll position.
     * If the user scrolls down and the topbar is currently shown, it hides the topbar.
     * If the user scrolls up and the topbar is currently hidden, it shows the topbar.
     */

    const handleScroll = () => {
      const scrollTop = window.scrollY;

      // Calculate the difference between current and previous scroll positions
      const scrollDiff = scrollTop - scrollTopPrev.current;

      // Action based on actual scroll direction
      if (scrollDiff > 0 && isShowTopbar) {
        hideTopbar();
      } else if (
        (scrollDiff < 0 || scrollTopPrev.current <= 0) &&
        !isShowTopbar
      ) {
        showTopbar();
      }

      // Update previous position after processing
      scrollTopPrev.current = scrollTop;
    };

    // Throttle to execute only once every 200ms
    const throttled = throttle(handleScroll, 250);

    window.addEventListener('scroll', throttled);

    return () => {
      window.removeEventListener('scroll', throttled);
      throttled.cancel();
    };
  }, [hideTopbar, isScrollListenerActive, isShowTopbar, showTopbar]);

  return (
    <TopbarContext.Provider value={memoizedValue}>
      {children}
    </TopbarContext.Provider>
  );
}

export default TopbarContextProvider;
